/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

/**
 * Realtime Contract - oRPC Room-Based Event Subscriptions
 *
 * This contract defines:
 * 1. WebSocket event payload schemas (used by both WS and SSE)
 * 2. Room management for targeted event delivery
 * 3. Connection and subscription management endpoints
 *
 * For SSE subscription endpoints, see subscriptions.contract.ts which provides
 * typed eventIterator-based streams.
 *
 * Event categories covered:
 * - Calendar Events: event_created, event_updated, event_cancelled, event_reinstated, event_completed, event_no_show
 * - Conversations: conversation_new_message, conversation_message_sync, etc.
 * - Streaming: conversation_stream_start, conversation_stream_chunk, etc.
 * - User Updates: user_document_updated, user_favorite_updated, etc.
 * - System: config_updated, service_status, vacation_period_updated
 */

import { eventIterator, oc } from "@orpc/contract";
import * as z from "zod";
import { AgentWorkflowStateSchema } from "./agent-workflow.contract";
import { ReminderSnapshotSchema } from "./conversations.contract";
import {
	BillingStatusUpdatedPayloadSchema,
	CampaignUpdatedPayloadSchema,
	QaPairUpdatedPayloadSchema,
	WaitingUsersUpdatedPayloadSchema,
	WaitlistUpdatedPayloadSchema,
	WhatsAppTemplateStatusPayloadSchema,
} from "./subscriptions.contract";

// =============================================================================
// Common Schemas
// =============================================================================

/**
 * Phone E164 format schema (canonical phone number format)
 * Example: +12025551234
 */
export const RealtimePhoneE164Schema = z
	.string()
	.regex(/^\+[1-9]\d{1,14}$/, "Phone number must be in E.164 format");

/**
 * Notification source enum - indicates where an event originated
 */
export const RealtimeNotificationSourceSchema = z.enum([
	"user",
	"secretary",
	"system",
	"ai",
	"whatsapp",
	"web",
	"api",
	"local",
	"agent",
	"external_user",
	"system_agent",
]);

/**
 * Common metadata fields present in most realtime events.
 *
 * The actor fields are stamped by the server for authenticated org-member
 * operations and survive schema validation on the wire copy, the persisted
 * /activity history, and web push (Zod strips undeclared keys, so they must
 * be declared here).
 */
export const RealtimeEventMetadataSchema = z.object({
	_source: z.string().nullish(),
	/** Acting org member's user id (absent for AI / external / system flows). */
	actor_user_id: z.string().nullish(),
	/** Acting org member's display label (email today). */
	actor_name: z.string().nullish(),
});

// =============================================================================
// Event Types Enumeration
// =============================================================================

/**
 * All possible realtime event types — canonical list.
 *
 * Previously re-exported from `shared-types/src/realtime-events.ts`, which
 * was deleted in commit b818a17b (Phase 6 cleanup) when contracts moved
 * here. The array now lives in this file as the single source of truth.
 */
export const REALTIME_EVENT_TYPES = [
	"event_created",
	"event_updated",
	"event_cancelled",
	"event_reinstated",
	"event_completed",
	"event_no_show",
	"conversation_new_message",
	"conversation_message_sync",
	"conversation_read",
	"conversation_cleared",
	"conversation_message_edited",
	"conversation_message_status",
	"conversation_message_deleted",
	"conversation_ai_toggled",
	"conversation_tool_approval_pending",
	"conversation_feedback",
	"vacation_period_updated",
	"user_document_updated",
	"user_favorite_updated",
	"user_active_updated",
	"user_phone_e164_changed",
	"user_name_updated",
	"contact_merged",
	"contact_created",
	"contact_updated",
	"contact_deleted",
	"contact_restored",
	"conversation_typing",
	"conversation_stream_start",
	"conversation_stream_chunk",
	"conversation_stream_sse",
	"conversation_stream_reasoning",
	"conversation_stream_end",
	"conversation_workflow_progress",
	"conversation_tool_call_started",
	"conversation_tool_call_completed",
	"conversation_tool_approval_requested",
	"conversation_tool_approval_responded",
	"config_updated",
	"ai_preset_updated",
	"custom_tool_updated",
	"snapshot",
	"metrics_updated",
	"service_status",
	"permissions_changed",
	"data_export_updated",
	"billing_status_updated",
	"org_activated",
	"org_assigned",
	"org_scope_changed",
	"whatsapp_template_status",
	"waitlist_updated",
	"waiting_users_updated",
	"campaign_updated",
	"qa_pair_updated",
] as const;

export type RealtimeEventType = (typeof REALTIME_EVENT_TYPES)[number];

/**
 * Streaming event types that bypass client filtering (critical for real-time UX)
 */
export const STREAMING_EVENT_TYPES = [
	"conversation_stream_start",
	"conversation_stream_chunk",
	"conversation_stream_end",
	"conversation_tool_call_started",
	"conversation_tool_call_completed",
] as const;

export type StreamingEventType = (typeof STREAMING_EVENT_TYPES)[number];

// =============================================================================
// Calendar Event Payloads
// =============================================================================

/**
 * Event Created - A new calendar event was scheduled
 */
export const CalendarEventCreatedPayloadSchema = z
	.object({
		id: z.union([z.number(), z.string()]).nullish(),
		event_id: z.union([z.number(), z.string()]).nullish(),
		phone_e164: RealtimePhoneE164Schema.optional(),
		contact_id: z.string().min(1).optional(),
		user_name: z.string().nullish(),
		eventDateTime: z.string(), // UTC datetime string
		// Exclusive end of the booking's interval. This mirror STRIPS undeclared
		// keys, so omitting it would drop the server's end and collapse a
		// multi-day bar to a single slot until the next refetch.
		endsAt: z.string().nullish(),
		type: z.string().nullish(),
		cancelled: z.boolean().nullish(),
		source: RealtimeNotificationSourceSchema.nullish(),
	})
	.merge(RealtimeEventMetadataSchema);

export type CalendarEventCreatedPayload = z.infer<
	typeof CalendarEventCreatedPayloadSchema
>;

/**
 * Event Updated - An existing calendar event was modified
 */
export const CalendarEventUpdatedPayloadSchema = z
	.object({
		id: z.union([z.number(), z.string()]).nullish(),
		event_id: z.union([z.number(), z.string()]).nullish(),
		phone_e164: RealtimePhoneE164Schema.optional(),
		contact_id: z.string().min(1).optional(),
		user_name: z.string().nullish(),
		eventDateTime: z.string(),
		// Exclusive end of the booking's interval. This mirror STRIPS undeclared
		// keys, so omitting it would drop the server's end and collapse a
		// multi-day bar to a single slot until the next refetch.
		endsAt: z.string().nullish(),
		type: z.string().nullish(),
		cancelled: z.boolean().nullish(),
		original_data: z
			.object({
				id: z.union([z.number(), z.string()]).nullish(),
				event_id: z.union([z.number(), z.string()]).nullish(),
				phone_e164: RealtimePhoneE164Schema.optional(),
				contact_id: z.string().min(1).optional(),
				user_name: z.string().nullish(),
				eventDateTime: z.string(),
				type: z.string().nullish(),
				cancelled: z.boolean().nullish(),
			})
			.nullish(),
		source: RealtimeNotificationSourceSchema.nullish(),
	})
	.merge(RealtimeEventMetadataSchema);

export type CalendarEventUpdatedPayload = z.infer<
	typeof CalendarEventUpdatedPayloadSchema
>;

/**
 * Event Cancelled - A calendar event was cancelled
 */
export const CalendarEventCancelledPayloadSchema = z
	.object({
		id: z.union([z.number(), z.string()]).nullish(),
		event_id: z.union([z.number(), z.string()]).nullish(),
		phone_e164: RealtimePhoneE164Schema.optional(),
		contact_id: z.string().min(1).optional(),
		user_name: z.string().nullish(),
		eventDateTime: z.string(),
		// Exclusive end of the booking's interval. This mirror STRIPS undeclared
		// keys, so omitting it would drop the server's end and collapse a
		// multi-day bar to a single slot until the next refetch.
		endsAt: z.string().nullish(),
		type: z.string().nullish(),
		cancelled: z.boolean().nullish(),
		has_document: z.boolean().nullish(),
		source: RealtimeNotificationSourceSchema.nullish(),
	})
	.merge(RealtimeEventMetadataSchema);

export type CalendarEventCancelledPayload = z.infer<
	typeof CalendarEventCancelledPayloadSchema
>;

/**
 * Event Reinstated - A previously cancelled event was restored
 */
export const CalendarEventReinstatedPayloadSchema = z
	.object({
		id: z.union([z.number(), z.string()]).nullish(),
		event_id: z.union([z.number(), z.string()]).nullish(),
		phone_e164: RealtimePhoneE164Schema.optional(),
		contact_id: z.string().min(1).optional(),
		user_name: z.string().nullish(),
		eventDateTime: z.string(),
		// Exclusive end of the booking's interval. This mirror STRIPS undeclared
		// keys, so omitting it would drop the server's end and collapse a
		// multi-day bar to a single slot until the next refetch.
		endsAt: z.string().nullish(),
		type: z.string().nullish(),
		cancelled: z.boolean().nullish(),
		has_document: z.boolean().nullish(),
		source: RealtimeNotificationSourceSchema.nullish(),
	})
	.merge(RealtimeEventMetadataSchema);

export type CalendarEventReinstatedPayload = z.infer<
	typeof CalendarEventReinstatedPayloadSchema
>;

/**
 * Event Completed - A calendar event was marked completed
 */
const CalendarEventStatusPayloadSchema = z
	.object({
		id: z.union([z.number(), z.string()]).nullish(),
		event_id: z.union([z.number(), z.string()]).nullish(),
		phone_e164: RealtimePhoneE164Schema.optional(),
		contact_id: z.string().min(1).optional(),
		user_name: z.string().nullish(),
		eventDateTime: z.string(),
		// Exclusive end of the booking's interval. This mirror STRIPS undeclared
		// keys, so omitting it would drop the server's end and collapse a
		// multi-day bar to a single slot until the next refetch.
		endsAt: z.string().nullish(),
		type: z.string().nullish(),
		cancelled: z.boolean().nullish(),
		status: z.string(),
		has_document: z.boolean().nullish(),
		source: RealtimeNotificationSourceSchema.nullish(),
	})
	.merge(RealtimeEventMetadataSchema);

// =============================================================================
// Conversation Message Payloads
// =============================================================================

/**
 * Message role enum
 */
export const MessageRoleSchema = z.enum([
	"user",
	"assistant",
	"secretary",
	"admin",
	"tool",
	"system",
]);

/** Channel identity for phone-based and platform-scoped conversations. */
export const RealtimeConversationChannelSchema = z.enum([
	"whatsapp",
	"sms",
	"email",
	"telegram",
	"instagram",
	"x",
	"messenger",
	"system_agent",
]);
export type RealtimeConversationChannel = z.infer<
	typeof RealtimeConversationChannelSchema
>;

/**
 * Base message payload shared between new message and sync events
 */
const ConversationMessageBaseSchema = z.object({
	message_id: z.string(),
	id: z.string().nullish(), // Some backend paths send 'id' instead of 'message_id'
	phone_e164: RealtimePhoneE164Schema.optional(),
	user_id: z.string().min(1).optional(),
	conversationId: z.string().min(1).optional(),
	// Channel-agnostic contact identity — how the web projection routes a
	// phone-less channel to its cached conversation.
	contactId: z.string().min(1).optional(),
	channel: RealtimeConversationChannelSchema.optional(),
	user_name: z.string().nullish(),
	role: MessageRoleSchema,
	message: z.string(),
	date: z.string(),
	time: z.string(),
	sentAt: z.string().nullish(), // ISO timestamp for proper sorting
	parts: z.array(z.unknown()).nullish(), // File attachments, images, etc.
	toolInvocations: z.array(z.unknown()).nullish(), // AI tool call sync
	reasoning: z.string().nullish(), // Reasoning content from AI model
	// Quoted-reply block carried on new-message / sync events so a live message
	// renders its WhatsApp-style quote without a refetch. Mirrors ReplyToSchema.
	replyTo: z
		.object({
			messageId: z.string().nullish(),
			externalMessageId: z.string().nullish(),
			preview: z.string(),
			role: z.string().nullish(),
		})
		.nullish(),
	// Platform message id of THIS row (WhatsApp wamid). The web react
	// affordance is gated on its presence, so live rows must carry it.
	externalMessageId: z.string().nullish(),
	reminder: ReminderSnapshotSchema.nullish(),
	// Soft-delete timestamp of THIS row — a sync carrying it removes the message
	// from the chat cache instead of upserting it.
	deletedAt: z.string().nullish(),
	telegram: z.record(z.string(), z.unknown()).nullish(),
	instagram: z.record(z.string(), z.unknown()).nullish(),
	x: z.record(z.string(), z.unknown()).nullish(),
});

/**
 * Conversation New Message - A new message received from a user (shows notifications)
 */
export const ConversationNewMessagePayloadSchema =
	ConversationMessageBaseSchema.merge(RealtimeEventMetadataSchema);

export type ConversationNewMessagePayload = z.infer<
	typeof ConversationNewMessagePayloadSchema
>;

/**
 * Conversation Message Sync - Message sent by secretary (UI sync only, no notifications)
 */
export const ConversationMessageSyncPayloadSchema =
	ConversationMessageBaseSchema.merge(RealtimeEventMetadataSchema);

export type ConversationMessageSyncPayload = z.infer<
	typeof ConversationMessageSyncPayloadSchema
>;

/** Private per-operator cursor update used to synchronize unread UI across tabs. */
export const ConversationReadPayloadSchema = z.object({
	orgId: z.string().min(1),
	userId: z.string().min(1),
	conversationId: z.string().regex(/^\d+$/),
	lastReadAt: z.string().datetime(),
});

export type ConversationReadPayload = z.infer<
	typeof ConversationReadPayloadSchema
>;

/**
 * Conversation Cleared - All messages in a conversation were deleted
 */
export const ConversationClearedPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema.optional(),
		conversationId: z.string().min(1).optional(),
		channel: z.string().min(1).optional(),
		user_id: z.string().nullish(),
		deleted: z.number().nullish(),
	})
	.merge(RealtimeEventMetadataSchema);

export type ConversationClearedPayload = z.infer<
	typeof ConversationClearedPayloadSchema
>;

/** A single reaction entry as carried on a conversation_message_edited event. */
export const ConversationReactionSchema = z.object({
	emoji: z.string(),
	direction: z.enum(["INBOUND", "OUTBOUND"]),
	sentAt: z.string(),
	externalMessageId: z.string().optional(),
});

/**
 * Conversation Message Edited - reused for edit/regenerate flows AND reaction
 * upserts. Edit/regenerate carries `from_message_id`/`edited_content`; a
 * reaction change carries `id`/`reactions` (the updated row, merged by id on the
 * web side) plus `role`/`message`/`sentAt` echoing the target.
 */
export const ConversationMessageEditedPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema,
		from_message_id: z.string(), // Message ID from which messages were deleted (inclusive)
		deleted: z.number().nullish(),
		edited_content: z.string().optional(), // New text content of the edited message (for cross-tab sync)
		// Reaction-upsert fields (absent on edit/regenerate broadcasts).
		id: z.string().optional(),
		message_id: z.string().optional(),
		reactions: z.array(ConversationReactionSchema).optional(),
		role: z.string().optional(),
		message: z.string().optional(),
		sentAt: z.string().optional(),
	})
	.merge(RealtimeEventMetadataSchema);

export type ConversationMessageEditedPayload = z.infer<
	typeof ConversationMessageEditedPayloadSchema
>;

/**
 * Conversation Message Status - a WhatsApp delivery receipt (sent -> delivered
 * -> read, or failed) for an OUTBOUND message. The web side advances the tick
 * indicator on the target row (matched by id, falling back to
 * externalMessageId) without a refetch. Absent on inbound turns.
 */
export const ConversationMessageStatusPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema.optional(),
		user_id: z.string().min(1).optional(),
		channel: RealtimeConversationChannelSchema.optional(),
		id: z.string().optional(),
		message_id: z.string().optional(),
		conversationId: z.string().optional(),
		externalMessageId: z.string().optional(),
		deliveryStatus: z.enum([
			"pending",
			"sending",
			"sent",
			"delivered",
			"read",
			"failed",
		]),
	})
	.merge(RealtimeEventMetadataSchema);

export type ConversationMessageStatusPayload = z.infer<
	typeof ConversationMessageStatusPayloadSchema
>;

/**
 * Conversation Message Deleted - a single message row was removed, either
 * soft-deleted locally by an operator or revoked by its sender on the platform.
 * Carries whichever identifier the deleting path knows (`id`/`message_id` for a
 * local row, `externalMessageId` for a provider revoke) plus the conversation
 * identity so a phone-less channel still routes.
 */
export const ConversationMessageDeletedPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema.optional(),
		user_id: z.string().min(1).optional(),
		contactId: z.string().min(1).optional(),
		conversationId: z.string().min(1).optional(),
		channel: RealtimeConversationChannelSchema.optional(),
		id: z.string().optional(),
		message_id: z.string().optional(),
		externalMessageId: z.string().optional(),
		deletedAt: z.string().nullish(),
	})
	.merge(RealtimeEventMetadataSchema);

export type ConversationMessageDeletedPayload = z.infer<
	typeof ConversationMessageDeletedPayloadSchema
>;

/**
 * Conversation Typing - User typing indicator state change
 */
export const ConversationTypingPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema.optional(),
		user_id: z.string().min(1).optional(),
		contactId: z.string().min(1).optional(),
		conversationId: z.string().min(1).optional(),
		channel: RealtimeConversationChannelSchema.optional(),
		channelAddress: z.string().min(1).optional(),
		timestamp: z.string().optional(),
		state: z.enum(["start", "stop"]),
	})
	.merge(RealtimeEventMetadataSchema);

export type ConversationTypingPayload = z.infer<
	typeof ConversationTypingPayloadSchema
>;

/**
 * Conversation AI Toggled - AI auto-reply enabled/disabled for a conversation.
 * Fired by the handoffToHuman tool, the operator UI toggle, or any code path
 * that flips conversation.aiEnabled.
 *
 * `phone_e164` is `z.string()` (not the strict E.164 schema) because messenger
 * contacts may have synthetic `messenger:<psid>` identifiers when no real phone
 * is on file. This event is a UI-state sync — the frontend uses the value
 * purely as a cache key, so any stable contact identifier is acceptable.
 */
export const ConversationAiToggledPayloadSchema = z
	.object({
		phone_e164: z.string().min(1),
		contact_id: z.string().optional(),
		user_name: z.string().optional(),
		channel: z.string(),
		ai_enabled: z.boolean(),
		reason: z.string().optional(),
	})
	.merge(RealtimeEventMetadataSchema);

/**
 * Conversation Tool Approval Pending - the customer assistant suspended a
 * `needsApproval` tool call (true) or the operator resolved the last pending
 * one (false). Same `phone_e164` relaxation rationale as
 * ConversationAiToggledPayloadSchema.
 */
export const ConversationToolApprovalPendingPayloadSchema = z
	.object({
		phone_e164: z.string().min(1),
		contact_id: z.string().nullish(),
		channel: z.string().nullish(),
		has_pending_approval: z.boolean(),
	})
	.merge(RealtimeEventMetadataSchema);

/**
 * Conversation Feedback - customer feedback recorded by the AI via the
 * saveFeedback tool. Same `phone_e164` relaxation rationale as
 * ConversationAiToggledPayloadSchema.
 */
export const ConversationFeedbackPayloadSchema = z
	.object({
		phone_e164: z.string().min(1),
		contact_id: z.string().optional(),
		user_name: z.string().optional(),
		channel: z.string(),
		summary: z.string(),
		quote: z.string(),
		sentiment: z.enum(["positive", "negative", "neutral"]),
	})
	.merge(RealtimeEventMetadataSchema);

export type ConversationAiToggledPayload = z.infer<
	typeof ConversationAiToggledPayloadSchema
>;

export type ConversationToolApprovalPendingPayload = z.infer<
	typeof ConversationToolApprovalPendingPayloadSchema
>;

export type ConversationFeedbackPayload = z.infer<
	typeof ConversationFeedbackPayloadSchema
>;

// =============================================================================
// AI Streaming Payloads
// =============================================================================

/**
 * AI usage cost tracking for stream end events
 */
export const RealtimeAiUsageCostSchema = z.object({
	inputTokens: z.number().int().nullish(),
	outputTokens: z.number().int().nullish(),
	totalTokens: z.number().int().nullish(),
	cachedInputTokens: z.number().int().nullish(),
	reasoningTokens: z.number().int().nullish(),
	modelId: z.string().nullish(),
	costUSD: z
		.object({
			inputUSD: z.number().nullish(),
			outputUSD: z.number().nullish(),
			cacheReadUSD: z.number().nullish(),
			reasoningUSD: z.number().nullish(),
			totalUSD: z.number().nullish(),
		})
		.nullish(),
	context: z
		.object({
			inputMax: z.number().nullish(),
			outputMax: z.number().nullish(),
			totalMax: z.number().nullish(),
			combinedMax: z.number().nullish(),
		})
		.nullish(),
});

export type RealtimeAiUsageCost = z.infer<typeof RealtimeAiUsageCostSchema>;

/**
 * Stream Start - Signals the start of a streaming AI response
 */
export const StreamStartPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema,
		stream_id: z.string(),
		role: z.literal("assistant"),
	})
	.merge(RealtimeEventMetadataSchema);

export type StreamStartPayload = z.infer<typeof StreamStartPayloadSchema>;

/**
 * Stream Chunk - Contains a chunk of streaming text
 */
export const StreamChunkPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema,
		stream_id: z.string(),
		chunk: z.string(),
	})
	.merge(RealtimeEventMetadataSchema);

export type StreamChunkPayload = z.infer<typeof StreamChunkPayloadSchema>;

/**
 * Stream SSE - Contains SSE-formatted streaming data (unified with System Agent)
 * Uses camelCase field names to match AI SDK v6 format
 */
export const StreamSsePayloadSchema = z
	.object({
		phoneE164: RealtimePhoneE164Schema,
		streamId: z.string(),
		chunk: z.string(),
	})
	.merge(RealtimeEventMetadataSchema);

export type StreamSsePayload = z.infer<typeof StreamSsePayloadSchema>;

/**
 * Stream Reasoning - Contains reasoning delta text from AI model
 */
export const StreamReasoningPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema,
		stream_id: z.string(),
		reasoning: z.string(),
	})
	.merge(RealtimeEventMetadataSchema);

export type StreamReasoningPayload = z.infer<
	typeof StreamReasoningPayloadSchema
>;

/**
 * Workflow Progress - live step tracker for the multi-agent RAG answering
 * pipeline (modes 4/5): research → draft → verify loop → revise. The route
 * key is relaxed (non-phone channels use a synthetic conversation key).
 */
export const WorkflowProgressPayloadSchema = z
	.object({
		phone_e164: z.string().min(1),
		workflow: AgentWorkflowStateSchema,
	})
	.merge(RealtimeEventMetadataSchema);

export type WorkflowProgressPayload = z.infer<
	typeof WorkflowProgressPayloadSchema
>;

/**
 * Stream End - Signals the end of a streaming response with final message
 */
export const StreamEndPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema,
		stream_id: z.string(),
		role: z.literal("assistant"),
		message: z.string(),
		date: z.string(),
		time: z.string(),
		message_id: z.string().nullish(),
		reasoning: z.string().nullish(),
		usage: RealtimeAiUsageCostSchema.nullish(),
	})
	.merge(RealtimeEventMetadataSchema);

export type StreamEndPayload = z.infer<typeof StreamEndPayloadSchema>;

// =============================================================================
// Tool Call Payloads
// =============================================================================

/**
 * Tool Call Started - An AI tool is being invoked
 */
export const ToolCallStartedPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema,
		stream_id: z.string(),
		tool_call_id: z.string(),
		tool_name: z.string(),
		tool_args: z.record(z.string(), z.unknown()).nullish(),
	})
	.merge(RealtimeEventMetadataSchema);

export type ToolCallStartedPayload = z.infer<
	typeof ToolCallStartedPayloadSchema
>;

/**
 * Tool Call Completed - An AI tool call has finished
 */
export const ToolCallCompletedPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema,
		stream_id: z.string(),
		tool_call_id: z.string(),
		tool_name: z.string(),
		tool_result: z.unknown().nullish(),
		tool_error: z.string().nullish(),
	})
	.merge(RealtimeEventMetadataSchema);

export type ToolCallCompletedPayload = z.infer<
	typeof ToolCallCompletedPayloadSchema
>;

/**
 * Tool Approval Requested - A tool requires user approval before execution
 */
export const ToolApprovalRequestedPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema,
		stream_id: z.string(),
		tool_call_id: z.string(),
		tool_name: z.string(),
		approval_id: z.string(),
		tool_args: z.record(z.string(), z.unknown()).nullish(),
	})
	.merge(RealtimeEventMetadataSchema);

export type ToolApprovalRequestedPayload = z.infer<
	typeof ToolApprovalRequestedPayloadSchema
>;

/**
 * Tool Approval Responded - Signals that a tool approval was approved or denied
 */
export const ToolApprovalRespondedPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema,
		message_id: z.string(),
		tool_call_id: z.string(),
		tool_name: z.string(),
		approved: z.boolean(),
	})
	.merge(RealtimeEventMetadataSchema);

export type ToolApprovalRespondedPayload = z.infer<
	typeof ToolApprovalRespondedPayloadSchema
>;

// =============================================================================
// User State Payloads
// =============================================================================

/**
 * User Document Updated - User's document attachment status changed.
 *
 * `phone_e164` is nullable and `contact_id` optional: a beneficiary is
 * phoneless by definition, so its document save can only be addressed by
 * contact id.
 */
export const UserDocumentUpdatedPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema.nullish(),
		contact_id: z.string().min(1).optional(),
		has_document: z.boolean(),
	})
	.merge(RealtimeEventMetadataSchema);

export type UserDocumentUpdatedPayload = z.infer<
	typeof UserDocumentUpdatedPayloadSchema
>;

/**
 * User Favorite Updated - User's favorite status changed
 */
export const UserFavoriteUpdatedPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema,
		favorite: z.boolean().nullish(),
	})
	.merge(RealtimeEventMetadataSchema);

export type UserFavoriteUpdatedPayload = z.infer<
	typeof UserFavoriteUpdatedPayloadSchema
>;

/**
 * User Active Updated - User's active status changed
 */
export const UserActiveUpdatedPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema,
		active: z.boolean().nullish(),
	})
	.merge(RealtimeEventMetadataSchema);

export type UserActiveUpdatedPayload = z.infer<
	typeof UserActiveUpdatedPayloadSchema
>;

/**
 * User Phone E164 Changed - User's phone number was updated
 */
export const UserPhoneE164ChangedPayloadSchema = z
	.object({
		old_phone_e164: RealtimePhoneE164Schema,
		new_phone_e164: RealtimePhoneE164Schema,
		user_name: z.string().nullish(),
		event_id: z.union([z.number(), z.string()]).nullish(),
	})
	.merge(RealtimeEventMetadataSchema);

export type UserPhoneE164ChangedPayload = z.infer<
	typeof UserPhoneE164ChangedPayloadSchema
>;

/**
 * User Name Updated - User's display name was changed
 */
export const UserNameUpdatedPayloadSchema = z
	.object({
		phone_e164: RealtimePhoneE164Schema,
		user_name: z.string(),
	})
	.merge(RealtimeEventMetadataSchema);

export type UserNameUpdatedPayload = z.infer<
	typeof UserNameUpdatedPayloadSchema
>;

/** Contact consolidated - a duplicate contact was absorbed into its survivor. */
export const ContactMergedPayloadSchema = z
	.object({
		orgId: z.string().min(1),
		loser_contact_id: z.string().min(1),
		winner_contact_id: z.string().min(1),
		winner_phone_e164: RealtimePhoneE164Schema.optional(),
		contact_id: z.string().min(1).optional(),
	})
	.merge(RealtimeEventMetadataSchema);

export type ContactMergedPayload = z.infer<typeof ContactMergedPayloadSchema>;

/**
 * Identity carried by every contact-row mutation broadcast. Tenant-wide like
 * `contact_merged` (the server's channel router routes both to the org-scoped
 * `subscriptions:all` channel), and self-sufficient so a client patches its
 * cached row without refetching the list. `phone_e164` is nullable — non-phone
 * channels (Instagram/Telegram/X/Messenger) have no phone alias.
 */
const ContactMutationBaseSchema = z
	.object({
		orgId: z.string().min(1),
		contact_id: z.string().min(1),
		phone_e164: RealtimePhoneE164Schema.nullish(),
		name: z.string().nullish(),
		channel: z.string().min(1).nullish(),
	})
	.merge(RealtimeEventMetadataSchema);

/** Contact created - a new contact row was added to the org. */
export const ContactCreatedPayloadSchema = ContactMutationBaseSchema;

export type ContactCreatedPayload = z.infer<typeof ContactCreatedPayloadSchema>;

/** Contact updated - an existing contact row's editable fields changed. */
export const ContactUpdatedPayloadSchema = ContactMutationBaseSchema;

export type ContactUpdatedPayload = z.infer<typeof ContactUpdatedPayloadSchema>;

/**
 * Contact deleted - a contact row was soft-deleted. The resulting soft-delete
 * state travels on the wire so the client flips the row in place.
 */
export const ContactDeletedPayloadSchema = ContactMutationBaseSchema.extend({
	deleted_at: z.string().nullish(),
	is_active: z.boolean().nullish(),
});

export type ContactDeletedPayload = z.infer<typeof ContactDeletedPayloadSchema>;

/** Contact restored - a soft-deleted contact row was brought back. */
export const ContactRestoredPayloadSchema = ContactMutationBaseSchema.extend({
	deleted_at: z.string().nullish(),
	is_active: z.boolean().nullish(),
});

export type ContactRestoredPayload = z.infer<
	typeof ContactRestoredPayloadSchema
>;

// =============================================================================
// System Payloads
// =============================================================================

/**
 * Config Updated - Application configuration was changed
 */
export const ConfigUpdatedPayloadSchema = z
	.object({
		updated_at: z.string().nullish(),
		sections: z.array(z.string()).nullish(), // Which sections were updated
		// The id of the client that initiated the write, echoed back so that
		// client can ignore its own realtime echo. The server stamps it on every
		// org config broadcast; undeclared here it is stripped, and the web
		// echo-guard falls back to a blanket time window that also swallows OTHER
		// members' config changes.
		origin_client_id: z.string().nullish(),
	})
	.merge(RealtimeEventMetadataSchema);

export type ConfigUpdatedPayload = z.infer<typeof ConfigUpdatedPayloadSchema>;

/** Agent preset created, edited, removed, activated, or duplicated. */
export const AiPresetUpdatedPayloadSchema = z.object({
	operation: z.enum([
		"created",
		"updated",
		"deleted",
		"activated",
		"duplicated",
		// A channel was pinned to (or unpinned from) this preset. Distinct from
		// "activated": the org default is unchanged, only one channel's override.
		"bound",
	]),
	presetId: z.string(),
	presetName: z.string(),
	activePresetId: z.string(),
});

export type AiPresetUpdatedPayload = z.infer<
	typeof AiPresetUpdatedPayloadSchema
>;

/**
 * Vacation Period Updated - Vacation schedule was modified
 */
export const VacationPeriodUpdatedPayloadSchema = z
	.object({
		periods: z.array(z.unknown()).nullish(),
		// Which mutation this broadcast represents — the activity feed reads
		// "Vacation added/updated/removed" from it instead of a single "updated".
		// Undeclared it is stripped, exactly as on the server schema.
		operation: z.enum(["added", "updated", "deleted"]).optional(),
	})
	.merge(RealtimeEventMetadataSchema);

export type VacationPeriodUpdatedPayload = z.infer<
	typeof VacationPeriodUpdatedPayloadSchema
>;

/**
 * Custom Tool Updated - a custom action (org-defined API tool) was mutated.
 * Broadcast org-room-wide so an open config page refetches the actions list.
 */
export const CustomToolUpdatedPayloadSchema = z
	.object({
		operation: z.enum(["created", "updated", "deleted", "toggled"]),
		toolId: z.string(),
		name: z.string(),
		enabled: z.boolean(),
	})
	.merge(RealtimeEventMetadataSchema);

export type CustomToolUpdatedPayload = z.infer<
	typeof CustomToolUpdatedPayloadSchema
>;

/**
 * Service Status - Backend service health status change
 */
export const ServiceStatusPayloadSchema = z
	.object({
		service: z.enum(["database", "redis", "backend"]),
		status: z.enum(["connected", "disconnected", "connecting", "degraded"]),
		reason: z.string().nullish(),
		message: z.string().nullish(),
		attemptNumber: z.number().nullish(), // Retry attempt number
		retryInSeconds: z.number().nullish(), // Next retry countdown
	})
	.merge(RealtimeEventMetadataSchema);

export type ServiceStatusPayload = z.infer<typeof ServiceStatusPayloadSchema>;

/**
 * Metrics Updated - System metrics were refreshed
 */
export const MetricsUpdatedPayloadSchema = z.record(z.string(), z.unknown());

export type MetricsUpdatedPayload = z.infer<typeof MetricsUpdatedPayloadSchema>;

/**
 * Snapshot - Full state snapshot for client sync
 */
export const SnapshotPayloadSchema = z.record(z.string(), z.unknown());

export type SnapshotPayload = z.infer<typeof SnapshotPayloadSchema>;

/**
 * Permissions Changed - a role write (create / update / delete) or a
 * membership write (custom role assigned / unassigned, member removed or
 * deactivated) changed who may do what inside ONE org.
 *
 * TENANT-scoped: it arrives on every member's stream, so the receiver must
 * decide for ITSELF whether it is affected — its own user id in
 * `affectedUserIds`, its own `Member` row id in `affectedMemberIds`, or the
 * role it holds in `roleId`. On a match the client dispatches the
 * `session:refresh` window event to re-enrich `user.permissions`.
 *
 * Both id lists are optional because the server producers only carry what
 * they already loaded: the role writes know `roleId` but never enumerate the
 * members holding it, the assign/unassign writes gate on the membership row
 * (no userId), and the team writes know the userId but no role.
 */
export const PermissionsChangedPayloadSchema = z
	.object({
		orgId: z.string().min(1),
		/**
		 * WHAT changed, for clients that branch coarsely: which role a member
		 * holds, the grants inside a role, or the membership itself.
		 */
		change: z.enum(["role_assignment", "permission_set", "membership"]),
		/** The producing operation — the fine-grained detail behind `change`. */
		reason: z.enum([
			"role_created",
			"role_updated",
			"role_deleted",
			"role_assigned",
			"role_unassigned",
			"member_removed",
			"member_active_changed",
		]),
		/** The written / assigned role; absent when there is no single one. */
		roleId: z.string().min(1).nullish(),
		/** Affected user ids — what the team (membership) writes have in hand. */
		affectedUserIds: z.array(z.string()).optional(),
		/** Affected `Member` row ids — what the roles writes have in hand. */
		affectedMemberIds: z.array(z.string()).optional(),
	})
	.merge(RealtimeEventMetadataSchema);

export type PermissionsChangedPayload = z.infer<
	typeof PermissionsChangedPayloadSchema
>;

/**
 * Org Scope Changed - a superadmin entered or exited org impersonation.
 * Delivered ONLY on that superadmin's org-less user-scoped channel, so it
 * reaches their other tabs (whose streams connected under the scope being
 * left) and no one else's.
 */
export const OrgScopeChangedPayloadSchema = z.object({
	/** `entered` = now acting inside `orgId`; `exited` = back to platform scope. */
	action: z.enum(["entered", "exited"]),
	/** The org entered; null on exit. */
	orgId: z.string().nullable(),
	orgName: z.string().nullish(),
	impersonationId: z.string().nullish(),
	/** The superadmin whose scope changed — i.e. the recipient. */
	userId: z.string().min(1).nullish(),
	_source: z.string().optional(),
});

export type OrgScopeChangedPayload = z.infer<
	typeof OrgScopeChangedPayloadSchema
>;

/**
 * Data Export Updated - a data-export request's lifecycle status changed
 * (RUNNING / READY / FAILED). Delivered only on the requesting user's
 * user-scoped channel, so receivers can assume the export is their own.
 * On receipt, the client refetches its export list instead of polling.
 */
export const DataExportUpdatedPayloadSchema = z.object({
	exportRequestId: z.string(),
	status: z.enum(["PENDING", "RUNNING", "READY", "EXPIRED", "FAILED"]),
	_source: z.string().optional(),
});

export type DataExportUpdatedPayload = z.infer<
	typeof DataExportUpdatedPayloadSchema
>;

/**
 * Org Activated - a superuser flipped the org-active toggle on. Delivered
 * only on each member's user-scoped channel (an inactive org's members lose
 * org scope at the auth boundary, so org-scoped channels cannot reach them).
 * On receipt, the client dispatches the `session:refresh` window event so
 * the pending-org page forwards into the app instead of polling.
 */
export const OrgActivatedPayloadSchema = z.object({
	orgId: z.string(),
	_source: z.string().optional(),
});

export type OrgActivatedPayload = z.infer<typeof OrgActivatedPayloadSchema>;

/**
 * Org Assigned - a superuser attached a waiting (org-less) user to an org.
 * Delivered only on the affected user's user-scoped channel, so receivers
 * can assume the assignment is their own. On receipt, the client dispatches
 * the `session:refresh` window event to pick up the new membership.
 */
export const OrgAssignedPayloadSchema = z.object({
	orgId: z.string(),
	_source: z.string().optional(),
});

export type OrgAssignedPayload = z.infer<typeof OrgAssignedPayloadSchema>;

// =============================================================================
// Unified Realtime Event Envelope
// =============================================================================

/**
 * Unified realtime event envelope for WebSocket/SSE streaming
 * This is the wire format for all realtime events
 */
export const RealtimeEventEnvelopeSchema = z.object({
	type: z.enum(REALTIME_EVENT_TYPES),
	data: z.unknown(),
	entities: z.array(z.string()).nullish(), // Entity IDs for filtering
	timestamp: z.string().nullish(), // ISO timestamp
});

export type RealtimeEventEnvelope = z.infer<typeof RealtimeEventEnvelopeSchema>;

// =============================================================================
// Room Management Schemas
// =============================================================================

/**
 * Room name patterns used for targeted event delivery
 * Format: "namespace:identifier" (e.g., "conversation:+1234567890")
 */
export const RoomNameSchema = z.string().min(1);

/**
 * Connection ID for WebSocket connections
 */
export const ConnectionIdSchema = z.string().uuid();

/**
 * Join room input - subscribe to a specific room
 */
export const JoinRoomInputSchema = z.object({
	room: RoomNameSchema,
});

export type JoinRoomInput = z.infer<typeof JoinRoomInputSchema>;

/**
 * Leave room input - unsubscribe from a specific room
 */
export const LeaveRoomInputSchema = z.object({
	room: RoomNameSchema,
});

export type LeaveRoomInput = z.infer<typeof LeaveRoomInputSchema>;

/**
 * Room membership response
 */
export const RoomMembershipSchema = z.object({
	room: RoomNameSchema,
	joined: z.boolean(),
	memberCount: z.number().int().nullish(),
});

export type RoomMembership = z.infer<typeof RoomMembershipSchema>;

/**
 * Connection info response
 */
export const ConnectionInfoSchema = z.object({
	connectionId: z.string(),
	userId: z.string().nullish(),
	connectedAt: z.string().datetime(),
	rooms: z.array(RoomNameSchema),
});

export type ConnectionInfo = z.infer<typeof ConnectionInfoSchema>;

// =============================================================================
// Client Filter Schemas
// =============================================================================

/**
 * Client-side event filtering configuration
 */
export const ClientFiltersSchema = z.object({
	types: z.array(z.enum(REALTIME_EVENT_TYPES)).nullish(),
	entities: z.array(z.string()).nullish(),
});

export type ClientFilters = z.infer<typeof ClientFiltersSchema>;

// =============================================================================
// Room Event Schema (for subscribeToRoom)
// =============================================================================

/**
 * Room event envelope - used for room-based subscriptions.
 * Contains the event type and payload for events delivered to a specific room.
 */
export const RoomEventSchema = z.object({
	type: z.enum(REALTIME_EVENT_TYPES),
	data: z.unknown(),
	room: RoomNameSchema,
	timestamp: z.string().datetime(),
});

export type RoomEvent = z.infer<typeof RoomEventSchema>;

/**
 * Subscribe to room input schema
 */
export const SubscribeToRoomInputSchema = z.object({
	room: RoomNameSchema,
});

export type SubscribeToRoomInput = z.infer<typeof SubscribeToRoomInputSchema>;

// =============================================================================
// Realtime Contract Definition
// =============================================================================

/**
 * Realtime oRPC Contract
 *
 * Provides endpoints for:
 * 1. Room management (join/leave conversation rooms)
 * 2. Connection status queries
 * 3. Event type filtering configuration
 *
 * For SSE subscription streams, see subscriptions.contract.ts
 */
export const realtimeContract = {
	/**
	 * Join a room to receive targeted events
	 * Typically used for conversation rooms: "conversation:+1234567890"
	 */
	joinRoom: oc
		.route({
			method: "POST",
			path: "/api/realtime/rooms/join",
			summary: "Join a realtime room",
			description:
				"Subscribes the current connection to a room for targeted event delivery. " +
				'Common pattern: "conversation:{phoneE164}" for chat rooms.',
			tags: ["Realtime"],
		})
		.input(JoinRoomInputSchema)
		.output(RoomMembershipSchema),

	/**
	 * Leave a room to stop receiving its events
	 */
	leaveRoom: oc
		.route({
			method: "POST",
			path: "/api/realtime/rooms/leave",
			summary: "Leave a realtime room",
			description: "Unsubscribes the current connection from a room.",
			tags: ["Realtime"],
		})
		.input(LeaveRoomInputSchema)
		.output(RoomMembershipSchema),

	/**
	 * Get current connection info including room memberships
	 */
	getConnectionInfo: oc
		.route({
			method: "GET",
			path: "/api/realtime/connection",
			summary: "Get connection info",
			description:
				"Returns the current WebSocket connection info including joined rooms.",
			tags: ["Realtime"],
		})
		.input(z.object({}))
		.output(ConnectionInfoSchema),

	/**
	 * Update client-side event filters
	 */
	setFilters: oc
		.route({
			method: "POST",
			path: "/api/realtime/filters",
			summary: "Set event filters",
			description:
				"Configures client-side event filtering. " +
				"Only events matching the filter criteria will be delivered.",
			tags: ["Realtime"],
		})
		.input(ClientFiltersSchema)
		.output(
			z.object({
				applied: z.boolean(),
				filters: ClientFiltersSchema,
			})
		),

	/**
	 * Subscribe to all realtime events (SSE endpoint)
	 * Returns an SSE stream of RealtimeEventEnvelope
	 */
	subscribe: oc
		.route({
			method: "GET",
			path: "/api/realtime/subscribe",
			summary: "Subscribe to realtime events (SSE)",
			description:
				"Establishes an SSE connection for receiving realtime events. " +
				"Events are filtered based on optional type and entity parameters.",
			tags: ["Realtime"],
		})
		.input(
			z.object({
				types: z.array(z.string()).optional(),
				entities: z.array(z.string()).optional(),
			})
		)
		.output(RealtimeEventEnvelopeSchema),

	/**
	 * Subscribe to a specific conversation room (SSE endpoint)
	 * Returns an SSE stream scoped to that conversation
	 */
	subscribeToConversation: oc
		.route({
			method: "GET",
			path: "/api/realtime/conversations/{phoneE164}/subscribe",
			summary: "Subscribe to conversation events (SSE)",
			description:
				"Establishes an SSE connection for receiving events for a specific conversation. " +
				"Includes messages, typing indicators, and streaming AI responses.",
			tags: ["Realtime", "Conversations"],
		})
		.input(
			z.object({
				phoneE164: RealtimePhoneE164Schema,
			})
		)
		.output(RealtimeEventEnvelopeSchema),

	/**
	 * Get service status for all monitored services
	 */
	getServiceStatus: oc
		.route({
			method: "GET",
			path: "/api/realtime/status",
			summary: "Get service status",
			description:
				"Returns the current status of all monitored backend services.",
			tags: ["Realtime", "System"],
		})
		.input(z.object({}))
		.output(
			z.object({
				services: z.array(ServiceStatusPayloadSchema.omit({ _source: true })),
				timestamp: z.string().datetime(),
			})
		),

	/**
	 * Get current state snapshot for client synchronization
	 */
	getSnapshot: oc
		.route({
			method: "GET",
			path: "/api/realtime/snapshot",
			summary: "Get current state snapshot",
			description:
				"Returns a snapshot of the current application state for client synchronization.",
			tags: ["Realtime"],
		})
		.input(z.object({}))
		.output(SnapshotPayloadSchema),

	/**
	 * Subscribe to a specific room's events (SSE/WebSocket endpoint)
	 * Returns an event stream for all events published to the room.
	 *
	 * Room name patterns:
	 * - "chat:+1234567890" - Subscribe to a specific conversation
	 * - "calendar" - Subscribe to calendar events
	 * - "system" - Subscribe to system-wide events
	 *
	 * This replaces the legacy WebSocket join_room/leave_room messages
	 * with a type-safe oRPC subscription.
	 */
	subscribeToRoom: oc
		.route({
			method: "GET",
			path: "/api/realtime/rooms/{room}/subscribe",
			summary: "Subscribe to room events (SSE)",
			description:
				"Establishes an SSE connection for receiving events for a specific room. " +
				'Supports room patterns like "chat:{phoneE164}" for conversations, ' +
				'"calendar" for calendar events, and "system" for system-wide events.',
			tags: ["Realtime", "Subscriptions"],
		})
		.input(SubscribeToRoomInputSchema)
		.output(eventIterator(RoomEventSchema)),
};

export type RealtimeContract = typeof realtimeContract;

// =============================================================================
// Payload Type Map (for type-safe event handling)
// =============================================================================

/**
 * Maps event types to their payload schemas
 * Used for type-safe event handling in handlers
 */
export const REALTIME_PAYLOAD_SCHEMAS = {
	event_created: CalendarEventCreatedPayloadSchema,
	event_updated: CalendarEventUpdatedPayloadSchema,
	event_cancelled: CalendarEventCancelledPayloadSchema,
	event_reinstated: CalendarEventReinstatedPayloadSchema,
	event_completed: CalendarEventStatusPayloadSchema,
	event_no_show: CalendarEventStatusPayloadSchema,
	conversation_new_message: ConversationNewMessagePayloadSchema,
	conversation_message_sync: ConversationMessageSyncPayloadSchema,
	conversation_read: ConversationReadPayloadSchema,
	conversation_cleared: ConversationClearedPayloadSchema,
	conversation_message_edited: ConversationMessageEditedPayloadSchema,
	conversation_message_status: ConversationMessageStatusPayloadSchema,
	conversation_message_deleted: ConversationMessageDeletedPayloadSchema,
	conversation_typing: ConversationTypingPayloadSchema,
	conversation_ai_toggled: ConversationAiToggledPayloadSchema,
	conversation_tool_approval_pending:
		ConversationToolApprovalPendingPayloadSchema,
	conversation_feedback: ConversationFeedbackPayloadSchema,
	conversation_stream_start: StreamStartPayloadSchema,
	conversation_stream_chunk: StreamChunkPayloadSchema,
	conversation_stream_sse: StreamSsePayloadSchema,
	conversation_stream_reasoning: StreamReasoningPayloadSchema,
	conversation_stream_end: StreamEndPayloadSchema,
	conversation_workflow_progress: WorkflowProgressPayloadSchema,
	conversation_tool_call_started: ToolCallStartedPayloadSchema,
	conversation_tool_call_completed: ToolCallCompletedPayloadSchema,
	conversation_tool_approval_requested: ToolApprovalRequestedPayloadSchema,
	conversation_tool_approval_responded: ToolApprovalRespondedPayloadSchema,
	user_document_updated: UserDocumentUpdatedPayloadSchema,
	user_favorite_updated: UserFavoriteUpdatedPayloadSchema,
	user_active_updated: UserActiveUpdatedPayloadSchema,
	user_phone_e164_changed: UserPhoneE164ChangedPayloadSchema,
	user_name_updated: UserNameUpdatedPayloadSchema,
	contact_merged: ContactMergedPayloadSchema,
	contact_created: ContactCreatedPayloadSchema,
	contact_updated: ContactUpdatedPayloadSchema,
	contact_deleted: ContactDeletedPayloadSchema,
	contact_restored: ContactRestoredPayloadSchema,
	config_updated: ConfigUpdatedPayloadSchema,
	ai_preset_updated: AiPresetUpdatedPayloadSchema,
	vacation_period_updated: VacationPeriodUpdatedPayloadSchema,
	custom_tool_updated: CustomToolUpdatedPayloadSchema,
	service_status: ServiceStatusPayloadSchema,
	metrics_updated: MetricsUpdatedPayloadSchema,
	snapshot: SnapshotPayloadSchema,
	permissions_changed: PermissionsChangedPayloadSchema,
	data_export_updated: DataExportUpdatedPayloadSchema,
	billing_status_updated: BillingStatusUpdatedPayloadSchema,
	org_activated: OrgActivatedPayloadSchema,
	org_assigned: OrgAssignedPayloadSchema,
	org_scope_changed: OrgScopeChangedPayloadSchema,
	whatsapp_template_status: WhatsAppTemplateStatusPayloadSchema,
	waitlist_updated: WaitlistUpdatedPayloadSchema,
	waiting_users_updated: WaitingUsersUpdatedPayloadSchema,
	campaign_updated: CampaignUpdatedPayloadSchema,
	qa_pair_updated: QaPairUpdatedPayloadSchema,
} as const;

/**
 * Type-safe payload lookup by event type
 */
export type RealtimePayloadByType = {
	[K in RealtimeEventType]: z.infer<(typeof REALTIME_PAYLOAD_SCHEMAS)[K]>;
};
